import { SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { UserRound } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Shared presentation + grouping helpers for the user selectors' dynamic "Me" option.
 *
 * When `showCurrentUser` is enabled (saved-views filter context only) the flat option list is
 * split into two labelled groups — the viewer-relative "Me" sentinel and the real people — so the
 * dynamic option reads as a distinct concept rather than just another row.
 */

const isCurrentUserOption = (value: bigint | number): boolean =>
    String(value) === String(SYSTEM_ACCOUNT_IDS.CURRENT_USER);

/** Uppercase section header rendered above a group of options. */
export const userOptionGroupHeader = (text: string): ReactNode => (
    <span className="font-semibold text-2xs text-subtle-foreground uppercase tracking-wider">{text}</span>
);

/**
 * Two-line dropdown content for the dynamic "Me" sentinel: an accent icon-circle (NOT the viewer's
 * own avatar, so it can't be confused with their named row, which still appears under "People") plus
 * a label and a subtitle naming the signed-in user.
 */
export const renderMeOption = (name: string): ReactNode => (
    <span className="flex items-center gap-2.5 py-0.5">
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-accent-500">
            <UserRound className="h-4 w-4 text-white" />
        </span>
        <span className="flex flex-col leading-tight">
            <span className="font-semibold text-accent-700">Me</span>
            <span className="font-normal text-accent-600 text-xs">Currently signed in as {name}</span>
        </span>
    </span>
);

// `searchValue?: never` keeps `option.searchValue` accessible across the leaf-or-group union that
// antd infers for `filterOption` (groups never reach it, but the property must exist on both sides).
type UserOptionGroup<T> = { label: ReactNode; title: string; options: T[]; searchValue?: never };

/**
 * Derive the antd `options` shape from a flat option list. Returns the list unchanged unless
 * `showCurrentUser` is set, in which case it is split into "Relative to you" (the Me sentinel) and
 * "People" groups. Keeping the source list flat lets callers keep their value/label lookups simple.
 */
export const buildUserOptionGroups = <T extends { value: bigint | number }>(
    flatOptions: T[] | undefined,
    showCurrentUser: boolean | undefined,
): (T | UserOptionGroup<T>)[] => {
    if (!showCurrentUser || !flatOptions) return flatOptions ?? [];

    const me = flatOptions.filter((option) => isCurrentUserOption(option.value));
    const people = flatOptions.filter((option) => !isCurrentUserOption(option.value));

    return [
        { label: userOptionGroupHeader('Relative to you'), title: 'Relative to you', options: me },
        ...(people.length ? [{ label: userOptionGroupHeader('People'), title: 'People', options: people }] : []),
    ];
};
